/**
 * 文件下载 URL 缓存 Hook
 *
 * @description 缓存 file_key 到 download_url 的映射，避免重复请求
 */

import { useState, useCallback, useRef } from 'react'
import { getFileDownloadUrl } from '@/api/biography'

interface CacheEntry {
  url: string
  timestamp: number
}

// 全局缓存，在应用生命周期内持久化
const urlCache = new Map<string, CacheEntry>()

// 缓存有效期：30 分钟
const CACHE_DURATION = 30 * 60 * 1000

/**
 * 使用文件下载 URL 的 Hook
 * @returns 获取下载 URL 的函数和加载状态
 */
export const useFileDownloadUrl = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // 用于取消请求
  const abortControllerRef = useRef<AbortController | null>(null)

  /**
   * 获取文件下载 URL，优先从缓存读取
   * @param fileKey 文件 key
   * @returns 下载 URL
   */
  const getDownloadUrl = useCallback(async (fileKey: string): Promise<string | null> => {
    if (!fileKey) {
      return null
    }

    // 检查缓存
    const cached = urlCache.get(fileKey)
    if (cached) {
      const now = Date.now()
      // 如果缓存未过期，直接返回
      if (now - cached.timestamp < CACHE_DURATION) {
        return cached.url
      }
      // 缓存过期，删除
      urlCache.delete(fileKey)
    }

    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    try {
      setIsLoading(true)
      setError(null)

      // 创建新的 AbortController
      abortControllerRef.current = new AbortController()

      const data = await getFileDownloadUrl(fileKey)
      const url = data.download_url

      // 存入缓存
      urlCache.set(fileKey, {
        url,
        timestamp: Date.now()
      })

      return url
    } catch (err) {
      const error = err instanceof Error ? err : new Error('获取下载 URL 失败')
      setError(error)
      console.error('获取下载 URL 失败:', error)
      return null
    } finally {
      setIsLoading(false)
      abortControllerRef.current = null
    }
  }, [])

  /**
   * 清除指定 file_key 的缓存
   */
  const clearCache = useCallback((fileKey?: string) => {
    if (fileKey) {
      urlCache.delete(fileKey)
    } else {
      urlCache.clear()
    }
  }, [])

  /**
   * 检查缓存是否存在且有效
   */
  const isCached = useCallback((fileKey: string): boolean => {
    const cached = urlCache.get(fileKey)
    if (!cached) return false

    const now = Date.now()
    return now - cached.timestamp < CACHE_DURATION
  }, [])

  return {
    getDownloadUrl,
    clearCache,
    isCached,
    isLoading,
    error
  }
}
